import React from 'react';
import { User, Mail, Phone, MapPin, Calendar, MessageSquare } from 'lucide-react';
import { useSimulateur } from '../SimulateurContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export const Step5Contact = () => {
  const { formData, updateFormData, goToNextStep, goToPrevStep } = useSimulateur();

  const isValid = formData.clientName && formData.email && formData.phone && formData.address && formData.city && formData.postalCode;

  const handleContinue = () => {
    if (isValid) {
      goToNextStep();
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
          Vos coordonnées
        </h2>
        <p className="text-stone-600">
          Pour recevoir votre devis et planifier l'intervention
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Name */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <User className="w-4 h-4 text-emerald-600" />
            Nom complet *
          </Label>
          <Input
            type="text"
            placeholder="Ex: Jean Dupont"
            value={formData.clientName}
            onChange={(e) => updateFormData('clientName', e.target.value)}
            data-testid="input-clientName"
            className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
          />
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
              <Mail className="w-4 h-4 text-emerald-600" />
              Email *
            </Label>
            <Input
              type="email"
              placeholder="votre@email.com"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              data-testid="input-email"
              className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
              <Phone className="w-4 h-4 text-emerald-600" />
              Téléphone *
            </Label>
            <Input
              type="tel"
              placeholder="06 XX XX XX XX"
              value={formData.phone}
              onChange={(e) => updateFormData('phone', e.target.value)}
              data-testid="input-phone"
              className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
            />
          </div>
        </div>

        {/* Address */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <MapPin className="w-4 h-4 text-emerald-600" />
            Adresse du bien *
          </Label>
          <Input
            type="text"
            placeholder="Ex: 15 rue de la République"
            value={formData.address}
            onChange={(e) => updateFormData('address', e.target.value)}
            data-testid="input-address"
            className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
          />
        </div>

        {/* City and Postal Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
              Ville *
            </Label>
            <Input
              type="text"
              placeholder="Marseille"
              value={formData.city}
              onChange={(e) => updateFormData('city', e.target.value)}
              data-testid="input-city"
              className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
              Code postal *
            </Label>
            <Input
              type="text"
              placeholder="13009"
              value={formData.postalCode}
              onChange={(e) => updateFormData('postalCode', e.target.value)}
              data-testid="input-postalCode"
              className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
            />
          </div>
        </div>

        {/* Preferred Date */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <Calendar className="w-4 h-4 text-emerald-600" />
            Date souhaitée d'intervention
          </Label>
          <Input
            type="date"
            value={formData.preferredDate}
            onChange={(e) => updateFormData('preferredDate', e.target.value)}
            data-testid="input-preferredDate"
            className="h-12 border-2 border-stone-200 focus:border-emerald-500 rounded-xl"
          />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <MessageSquare className="w-4 h-4 text-emerald-600" />
            Message (optionnel)
          </Label>
          <textarea
            placeholder="Informations complémentaires, contraintes horaires..."
            value={formData.message}
            onChange={(e) => updateFormData('message', e.target.value)}
            data-testid="input-message"
            rows={3}
            className="w-full px-4 py-3 border-2 border-stone-200 focus:border-emerald-500 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={goToPrevStep}
            className="px-6 py-3 rounded-xl"
            data-testid="btn-prev-step5"
          >
            Retour
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!isValid}
            className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            data-testid="btn-next-step5"
          >
            Voir le récapitulatif
          </Button>
        </div>
      </div>
    </div>
  );
};
